import { useState } from 'react';
import type { InvestmentAllocationResponse } from 'shared';

import { useInvestmentPortfolio } from '../../features/investmentTheses/portfolioQueries';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';

import { InvestmentCurrencyPortfolio } from './InvestmentCurrencyPortfolio';

function coverageMessage(account: InvestmentAllocationResponse): string {
  switch (account.valuation.unavailableReason) {
    case 'UNSUPPORTED_SOURCE':
      return 'Unsupported valuation source; not included in totals.';
    case 'NO_HOLDINGS_SNAPSHOT':
      return 'No holdings snapshot; import holdings to include this account.';
    case 'CURRENCY_MISMATCH':
      return 'Snapshot currency differs from account currency; no verified FX conversion.';
    case 'INCONSISTENT_TOTALS':
      return 'Snapshot totals do not reconcile; excluded from totals.';
    case 'NON_POSITIVE_ACCOUNT_VALUE':
      return 'Non-positive account value; excluded from totals.';
    default:
      return account.valuation.stale
        ? 'Stale snapshot; included in totals.'
        : 'Snapshot included in totals.';
  }
}

export function InvestmentPortfolioDashboard({
  onSelectAccount,
}: {
  onSelectAccount: (accountId: string) => void;
}) {
  const query = useInvestmentPortfolio();
  const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null);
  const portfolio =
    query.data?.currencies.find((row) => row.currency === selectedCurrency) ??
    query.data?.currencies[0];
  const excluded = query.data?.accounts.filter((account) => !account.valuation.available) ?? [];
  return (
    <Card aria-label="Long-term portfolio dashboard">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">Long-term portfolio</h2>
          <p className="mt-1 text-sm text-text-muted">
            All accounts classified as Long-Term Investing. Trading accounts are excluded.
          </p>
        </div>
        <Button
          size="sm"
          variant="secondary"
          disabled={query.isFetching}
          onClick={() => {
            void query.refetch();
          }}
        >
          {query.isFetching ? 'Refreshing...' : 'Refresh portfolio'}
        </Button>
      </div>
      {query.isPending ? (
        <p className="text-text-muted">Loading long-term portfolio...</p>
      ) : query.isError || !query.data ? (
        <p role="alert" className="text-danger">
          Could not load the aggregate portfolio. Refresh to try again.
        </p>
      ) : (
        <div className="space-y-5">
          {excluded.length > 0 ? (
            <p role="status" className="text-sm text-warn">
              Partial coverage: {excluded.length} of {query.data.accounts.length} long-term accounts
              excluded. Totals and weights cover only valued accounts in the selected currency.
            </p>
          ) : null}
          <details className="rounded border border-border p-3 text-sm">
            <summary className="cursor-pointer font-medium">Valuation coverage and dates</summary>
            <ul className="mt-3 space-y-2">
              {query.data.accounts.map((account) => (
                <li key={account.account.id} className="break-words">
                  <span className="font-medium">
                    {account.account.name} ({account.account.currency})
                  </span>
                  {' - '}
                  {coverageMessage(account)}
                  {account.valuation.reportedAsOf
                    ? ` Reported ${account.valuation.reportedAsOf.localDateTime.replace('T', ' ')} UK time.`
                    : ''}
                </li>
              ))}
            </ul>
            <p className="mt-3 text-text-muted">
              Latest imported snapshots, not live prices. Dates can differ between accounts.
              Identical HL security codes and normalised company names are combined; differing
              identities remain separate.
            </p>
          </details>
          {query.data.currencies.length > 1 ? (
            <label className="block text-sm text-text-muted">
              Portfolio currency
              <select
                className="ml-2 rounded border border-border bg-bg px-3 py-2 text-text"
                value={portfolio?.currency ?? ''}
                onChange={(event) => setSelectedCurrency(event.target.value)}
              >
                {query.data.currencies.map((row) => (
                  <option key={row.currency} value={row.currency}>
                    {row.currency}
                  </option>
                ))}
              </select>
              <span className="mt-1 block">
                Currencies are kept separate. No unverified FX conversions.
              </span>
            </label>
          ) : null}
          {query.data.accounts.some((account) => account.valuation.stale) ? (
            <p role="status" className="text-sm text-warn">
              Stale valuations are included. Import fresh holdings before relying on allocation or
              P&amp;L.
            </p>
          ) : null}
          {portfolio ? (
            <InvestmentCurrencyPortfolio
              key={portfolio.currency}
              portfolio={portfolio}
              accountReturns={query.data.accountReturns}
              onSelectAccount={onSelectAccount}
            />
          ) : (
            <p className="text-sm text-text-muted">
              No supported portfolio valuations yet. Import HL holdings snapshots to begin.
            </p>
          )}
        </div>
      )}
    </Card>
  );
}
